import React, {useState, useEffect, useContext} from "react";
import Api from "../../Api";
import {useParams} from "react-router-dom";
import {withAuth} from "../../hoc/Auth";
import {Column, Container, Wrapper} from "../../hoc/Layout";
import EmptyCard from "../../hoc/EmptyCard";
import PayControls from "../../Components/PayControls";
import {InputHelper, InputAmountCurrencyHelper, Select} from "../../Components/Utils/FieldsHelper";
import {FormattedMessage} from "react-intl";

import classes from "../Pay/styles.module.css";

import MessageContext from "../../MessageContext";
import Context from "../../Context";
import ErrorHandler from "../../Services/ErrorHandler";
import ApiResponseValidator from "../../Services/ApiResponseValidator";
import MessageFormatter from "../../Services/MessageFormatter";
import RetryManager from "../../Services/RetryManager";

export default withAuth(({token}) => {

        const [projectUUID, setProjectUUID] = useState(useParams().uuid);
        const [providerUUID, setProviderUUID] = useState(null);

        const [projects, setProjects] = useState([]);
        const [terminals, setTerminals] = useState([]);
        const [selectedProject, setSelectedProject] = useState(null);
        const [selectedTerminal, setSelectedTerminal] = useState(null);

        const [totalFee, setTotalFee] = useState({fixedPerTransaction: 0, cost: 1, costFixedMinimum: 40});
        const [commission, setCommission] = useState(0);
        const [terminalBalance, setTerminalBalance] = useState({balance: 0, currency: "RUB"});

        const [form, setForm] = useState({phone: '', bic: '', cardHolder: '', amount: ''});

        const [loadingProjects, setLoadingProjects] = useState(true);
        const [loadingTerminals, setLoadingTerminals] = useState(false);

        const [isRequest, setIsRequest] = useState(false);

        const {onMessage} = useContext(MessageContext);

        useEffect(() => {
            const requestKey = 'sbp_pay_load_projects';

            const loadProjects = async () => {
                const retryInfo = RetryManager.canRetry(requestKey, { maxRetries: 3, baseDelay: 500 });
                if (!retryInfo.canRetry) {
                    ErrorHandler.logError(
                        new Error('Max retries exceeded for projects'),
                        { action: 'loadProjects', requestKey, reason: retryInfo.reason }
                    );
                    onMessage(MessageFormatter.error('Failed to load projects after multiple attempts'));
                    setLoadingProjects(false);
                    return;
                }

                try {
                    const projectsList = await new Api({token}).getProjects();
                    ApiResponseValidator.validateListResponse({data: projectsList});

                    RetryManager.clearRequest(requestKey);
                    setProjects(projectsList);
                } catch (error) {
                    RetryManager.recordAttempt(requestKey);

                    ErrorHandler.logError(error, { action: 'loadProjects', requestKey });
                    onMessage(MessageFormatter.error('Failed to load projects'));
                } finally {
                    setLoadingProjects(false);
                }
            };

            loadProjects();
            return () => RetryManager.clearRequest(requestKey);
        }, [token, onMessage]);

        function calcCommission(amount, fee) {
            if (!amount || amount.length === 0 || !fee) {
                return 0;
            }
            const cost = Number(fee.cost) || 0;
            const costFixedMinimum = Number(fee.costFixedMinimum) || 0;
            const fixedPerTransaction = Number(fee.fixedPerTransaction) || 0;
            const amountNum = Number(amount) || 0;

            const percentFee = amountNum * (cost / 100);
            if (percentFee < costFixedMinimum) {
                return costFixedMinimum + fixedPerTransaction;
            }
            return percentFee + fixedPerTransaction;
        }

        function onSelectProject(project) {
            const requestKey = `sbp_pay_load_project_${project.uuid}`;

            const loadTerminals = async () => {
                const retryInfo = RetryManager.canRetry(requestKey, { maxRetries: 3, baseDelay: 500 });
                if (!retryInfo.canRetry) {
                    ErrorHandler.logError(
                        new Error('Max retries exceeded for project data'),
                        { action: 'loadProjectData', projectUUID: project.uuid, requestKey, reason: retryInfo.reason }
                    );
                    onMessage(MessageFormatter.error('Failed to load terminal data after multiple attempts'));
                    setLoadingTerminals(false);
                    return;
                }

                try {
                    setProjectUUID(project.uuid);
                    setSelectedTerminal(null);
                    setProviderUUID(null);
                    setSelectedProject(project);
                    setLoadingTerminals(true);
                    setCommission(0);
                    setTerminalBalance({balance: 0, currency: "RUB"});

                    const projectData = await new Api({token}).getProjectData(project.uuid);
                    ApiResponseValidator.validateResponse(projectData);

                    RetryManager.clearRequest(requestKey);

                    if (projectData.terminals.length === 1) {
                        const terminal = projectData.terminals[0];
                        setTerminalBalance({balance: terminal.balance.confirmed, currency: terminal.currency});
                        setProviderUUID(terminal.uuid);
                        setSelectedTerminal(terminal);
                        setCommission(calcCommission(form.amount, terminal.fee.payout));
                        setTotalFee(terminal.fee.payout);
                    }

                    setTerminals(projectData.terminals);
                } catch (error) {
                    RetryManager.recordAttempt(requestKey);

                    ErrorHandler.logError(error, { action: 'loadProjectData', projectUUID: project.uuid, requestKey });
                    onMessage(MessageFormatter.error('Failed to load terminal data. Please try again.'));
                    setSelectedProject(null);
                    setTerminals([]);
                } finally {
                    setLoadingTerminals(false);
                }
            };
            loadTerminals();
        }

        function onSelectTerminal(terminal) {
            setSelectedTerminal(terminal);
            setProviderUUID(terminal.uuid);
            setTerminalBalance({balance: terminal.balance.confirmed, currency: terminal.currency})
            setTotalFee(terminal.fee.payout);

            if (form.amount.length > 0)
                setCommission(calcCommission(form.amount, terminal.fee.payout));
        }

        function onChangeForm(name, value) {
            setForm({...form, [name]: value});

            if (name === "amount")
                setCommission(calcCommission(value, totalFee))
        }

        async function onSendForm() {
            try {
                if (!projectUUID || !providerUUID) {
                    onMessage(MessageFormatter.warning('Please select a project and terminal for payment'));
                    return;
                }

                const totalCost = Number(form.amount) + Number(commission);
                if (Number(terminalBalance.balance) < totalCost) {
                    onMessage(MessageFormatter.warning('Insufficient funds. Required: ' + totalCost + ' ' + terminalBalance.currency));
                    return;
                }

                if (!form.phone || !form.bic || !form.cardHolder || !form.amount) {
                    onMessage(MessageFormatter.warning('Please fill all required fields: phone, bank ID, recipient name, and amount'));
                    return;
                }

                setIsRequest(true);

                const payoutData = {
                    payout_type: 'mobile',
                    phone: form.phone,
                    bic: form.bic,
                    cardHolder: form.cardHolder,
                    amount: form.amount,
                    providerUUID
                };

                const response = await new Api({token}).makeSinglePayout(projectUUID, payoutData);

                if (!response.transactionId) {
                    throw new Error('Missing transaction ID in response');
                }

                if (response.error === true && response.transactionId) {
                    onMessage(MessageFormatter.warning(
                        'Payment created but may have issues. Transaction ID: ' + response.transactionId
                    ));
                } else if (response.error === false && response.transactionId) {
                    onMessage(MessageFormatter.success(
                        'Payment successful! Transaction ID: ' + response.transactionId
                    ));
                } else if (response.code) {
                    throw new Error(response.message || 'Payment processing failed with code ' + response.code);
                } else {
                    throw new Error('Unexpected payment response');
                }

                setForm({phone: '', bic: '', cardHolder: '', amount: ''});
                setCommission(0);

            } catch (error) {
                const errorInfo = ErrorHandler.categorizeError(error);
                ErrorHandler.logError(error, {
                    action: 'makeSbpPayout',
                    projectUUID,
                    providerUUID
                });

                if (errorInfo.type === 'permission_denied') {
                    onMessage(MessageFormatter.permissionError('perform SBP payouts', 'this merchant'));
                } else if (errorInfo.type === 'validation') {
                    onMessage(MessageFormatter.validationError(error.message));
                } else {
                    onMessage(MessageFormatter.error(
                        ErrorHandler.getMessageForError(error, 'payout')
                    ));
                }
            } finally {
                setIsRequest(false);
            }
        }

        return (
            <Wrapper>
                <Context.Provider value={{onSelectProject}}>
                    <Container className={classes.StackOnMobile}>
                        <Column width="780" className={classes.MainColumn}>
                            <EmptyCard title="SbpPay.title">
                                <div className={classes.Block}>
                                    <div className={classes.Col_1}>
                                        <h4 className={classes.BlockTitle}><FormattedMessage id="SbpPay.label.recipient" defaultMessage="Получатель (СБП)"/></h4>
                                    </div>

                                    <div className={classes.Col_2}>
                                        <InputHelper
                                            onChange={onChangeForm}
                                            value={form.phone}
                                            placeholder={"79001234567"}
                                            fieldName={"phone"}
                                            label={"Pay.label.recipientPhone"}
                                            changeRegexp={/[^0-9]+/g}
                                            blurRegexp={/^\d{10,15}$/g}
                                            changeMessage={"FieldsHelper.error.onlyNumbersPhone"}
                                            blurMessage={"Pay.error.checkPhoneFormat"}
                                            isDisabled={isRequest}
                                            autoComplete="off"
                                        />
                                    </div>

                                    <div className={classes.Col_3}>
                                        <InputHelper
                                            onChange={onChangeForm}
                                            value={form.bic}
                                            placeholder={"044525225 или 1crt88888881"}
                                            fieldName={"bic"}
                                            label={"Pay.label.bicOrMemberId"}
                                            blurRegexp={/^.{1,64}$/g}
                                            blurMessage={"Pay.error.checkBicOrMemberId"}
                                            isDisabled={isRequest}
                                            autoComplete="off"
                                        />
                                    </div>
                                </div>

                                <div className={classes.Block}>
                                    <div className={classes.Col_1}>
                                        <h4 className={classes.BlockTitle}><FormattedMessage id="Pay.label.recipientName" defaultMessage="ФИО получателя"/></h4>
                                    </div>

                                    <div className={classes.Col_2_3}>
                                        <InputHelper
                                            onChange={onChangeForm}
                                            value={form.cardHolder}
                                            placeholder={"ИВАНОВ ИВАН ИВАНОВИЧ"}
                                            fieldName={"cardHolder"}
                                            label={"Pay.label.recipientFio"}
                                            blurRegexp={/^.{3,}$/g}
                                            blurMessage={"Pay.error.checkFio"}
                                            isUppercase
                                            isDisabled={isRequest}
                                            autoComplete="off"
                                        />
                                    </div>
                                </div>

                                <div className={classes.Divider}/>

                                <div className={classes.Block}>
                                    <div className={classes.Col_1}>
                                        <h4 className={classes.BlockTitle}><FormattedMessage id="Pay.label.amount" defaultMessage="Сумма"/></h4>
                                    </div>

                                    <div className={classes.Col_2}>

                                        <InputAmountCurrencyHelper
                                            onChange={onChangeForm}
                                            value={form.amount}
                                            currency={[terminalBalance.currency]}
                                            placeholder={"1960.20"}
                                            label={"FieldsHelper.label.amount"}
                                            changeMessage={"FieldsHelper.error.onlyNumbersAmount"}
                                            blurMessage={"FieldsHelper.error.checkAmount"}
                                            isDisabled={isRequest}
                                            autoComplete="off"
                                        />

                                    </div>
                                </div>

                            </EmptyCard>
                        </Column>

                        <Column width="350" className={classes.SideColumn}>
                            <EmptyCard>
                                <Select
                                    label={"FieldsHelper.label.project"}
                                    placeholder={"FieldsHelper.label.selectProject"}
                                    isLoading={loadingProjects || isRequest}
                                    values={projects}
                                    onChange={onSelectProject}
                                    selectedValue={selectedProject}
                                    fieldName={"displayName"}
                                    fieldStatus={"status"}
                                />

                                <Select
                                    label={"FieldsHelper.label.terminal"}
                                    placeholder={"FieldsHelper.label.selectTerminal"}
                                    isLoading={loadingProjects || !selectedProject || loadingTerminals}
                                    values={terminals}
                                    onChange={onSelectTerminal}
                                    selectedValue={selectedTerminal}
                                    isClean={false}
                                    fieldName={"name"}
                                    fieldStatus={"enabled"}
                                />
                            </EmptyCard>

                            <PayControls
                                name={name}
                                cardHolder={form.cardHolder}
                                phone={form.phone}
                                amount={form.amount ? form.amount : 0}
                                commission={commission}
                                balance={terminalBalance}
                                onSendForm={onSendForm}
                                isLoading={isRequest}
                            />
                        </Column>
                    </Container>
                </Context.Provider>
            </Wrapper>
        )
    }
)



// WEBPACK FOOTER //
// ./src/PagesOfUser/SbpPay/index.js